import React, { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import { socketService, NotificationData, QuorumStatus, VotingSessionData, VotingResults, ChatMessage, PresenceUser } from '../services/socketService';
import { apiService } from '../services/api';

const isRealtimeTraceEnabled = (): boolean => localStorage.getItem('traceRealtime') === '1';

const traceRealtime = (event: string, payload?: Record<string, unknown>): void => {
  if (!isRealtimeTraceEnabled()) return;
  console.log(`[TRACE_RT_CTX][${new Date().toISOString()}] ${event}`, payload || {});
};

// Tipos para el contexto
interface SocketContextType {
  // Estado de conexión
  isConnected: boolean;
  currentAssembly: string | null;
  connectionError: string | null;
  
  // Métodos de conexión
  connect: () => Promise<void>;
  disconnect: () => void;
  reconnect: () => void;
  
  // Métodos de asamblea
  joinAssembly: (assemblyId: string) => Promise<void>;
  leaveAssembly: (assemblyId: string) => Promise<void>;
  
  // Estado en tiempo real
  quorumStatus: QuorumStatus | null;
  activeVotingSession: VotingSessionData | null;
  activeSessions: VotingSessionData[]; // ✅ Lista de todas las sesiones activas
  votingResults: VotingResults | null;
  notifications: NotificationData[];
  chatMessages: ChatMessage[];
  typingUsers: { userId: string; userName: string; assemblyId?: string }[];
  presenceUsers: PresenceUser[];
  participantsCount: number;
  
  // Eventos de asamblea
  lastAssemblyStartedId: string | null; // 🆕 Último ID de asamblea iniciada
  lastAssemblyCompletedId: string | null; // 🆕 Último ID de asamblea completada
  
  // 🆕 Métodos para limpiar eventos de asamblea (prevenir loops infinitos)
  clearAssemblyStartedEvent: () => void;
  clearAssemblyCompletedEvent: () => void;
  
  // Métodos de chat
  sendMessage: (assemblyId: string, message: string) => Promise<void>;
  setTyping: (assemblyId: string, isTyping: boolean) => void;
  loadHistoricalMessages: (messages: ChatMessage[]) => void; // 🔥 Nuevo método
  
  // Métodos de votación (DEPRECADOS - resultados solo al finalizar)
  subscribeToVoting: (votingSessionId: string) => void; // ⚠️ Deprecado
  unsubscribeFromVoting: (votingSessionId: string) => void; // ⚠️ Deprecado
  
  // Métodos de presencia
  updatePresence: (assemblyId: string, status: 'present' | 'absent') => void;
  
  // Métodos de notificaciones
  markNotificationAsRead: (notificationId: string) => void;
  clearNotifications: () => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

interface SocketProviderProps {
  children: ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const RESYNC_COOLDOWN_MS = 1500;

  // Estados del socket
  const [isConnected, setIsConnected] = useState(false);
  const [currentAssembly, setCurrentAssembly] = useState<string | null>(null);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  
  // Estados de datos en tiempo real
  const [quorumStatus, setQuorumStatus] = useState<QuorumStatus | null>(null);
  const [activeVotingSession, setActiveVotingSession] = useState<VotingSessionData | null>(null);
  const [activeSessions, setActiveSessions] = useState<VotingSessionData[]>([]); // ✅ Lista de todas las sesiones activas
  const [votingResults, setVotingResults] = useState<VotingResults | null>(null);
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [typingUsers, setTypingUsers] = useState<{ userId: string; userName: string; assemblyId?: string }[]>([]);
  const [presenceUsers, setPresenceUsers] = useState<PresenceUser[]>([]);
  const [participantsCount, setParticipantsCount] = useState<number>(0);
  
  // 🆕 Estados de eventos de asamblea (para auto-join/leave)
  const [lastAssemblyStartedId, setLastAssemblyStartedId] = useState<string | null>(null);
  const [lastAssemblyCompletedId, setLastAssemblyCompletedId] = useState<string | null>(null);
  const votingResyncInFlightRef = useRef<boolean>(false);
  const lastVotingResyncAtRef = useRef<number>(0);

  // Resincroniza snapshot de votaciones activas para cubrir reconexiones donde pudieron perderse eventos.
  const syncLiveVotingState = useCallback(async (
    assemblyId: string,
    options?: { force?: boolean; reason?: string }
  ): Promise<void> => {
    if (!assemblyId) return;

    const now = Date.now();
    const force = options?.force === true;
    if (!force && now - lastVotingResyncAtRef.current < RESYNC_COOLDOWN_MS) {
      return;
    }

    if (votingResyncInFlightRef.current) {
      return;
    }

    lastVotingResyncAtRef.current = now;
    votingResyncInFlightRef.current = true;

    try {
      console.log('🔄 [SocketContext] Resincronizando votaciones activas:', {
        assemblyId,
        reason: options?.reason || 'unspecified',
      });

      const response = await apiService.getVotingSessionsLiveView(assemblyId);
      const sessionsData = response.data?.data || response.data?.sessions || response.data;
      const active = Array.isArray(sessionsData)
        ? sessionsData.filter((session: any) => session.status === 'active')
        : [];

      setActiveSessions(active);
      setActiveVotingSession((previousSession) => {
        if (active.length === 0) {
          return null;
        }

        if (previousSession?.id) {
          const matched = active.find((session: any) => session.id === previousSession.id);
          if (matched) {
            return matched;
          }
        }

        return active[0];
      });

      if (active.length > 0) {
        const primarySession = active[0];
        try {
          const resultsResponse = await apiService.getVotingLiveStats(primarySession.id);
          const currentResults = resultsResponse.data?.data || resultsResponse.data;

          if (currentResults) {
            setVotingResults(currentResults);
          }
        } catch {
          setVotingResults((previousResults) => {
            if (previousResults?.votingSessionId === primarySession.id) {
              return previousResults;
            }

            return {
              votingSessionId: primarySession.id,
              status: 'active',
              totalEligibleVoters: 0,
              totalVotes: primarySession.totalVotes || 0,
              totalCoefficient: 0,
              votedCoefficient: 0,
              participationRate: 0,
              quorumMet: false,
              results: [],
            };
          });
        }
      }
    } catch (error) {
      console.error('❌ [SocketContext] Error resincronizando votaciones activas:', error);
    } finally {
      votingResyncInFlightRef.current = false;
    }
  }, []);

  // Si hay sesiones activas pero aún no se setea activeVotingSession (p.ej. admin se une tarde), usar la primera como fallback
  useEffect(() => {
    if (!activeVotingSession && activeSessions.length > 0) {
      setActiveVotingSession(activeSessions[0]);
    }
  }, [activeVotingSession, activeSessions]);

  useEffect(() => {
    // Configurar listeners del socket
    setupSocketListeners();
    
    // NO conectar automáticamente - esperar a que AuthContext lo haga cuando el usuario esté autenticado
    console.log('🔌 [SocketContext] Listeners configurados, esperando conexión desde AuthContext...');

    // Cleanup al desmontar
    return () => {
      socketService.removeAllListeners();
      socketService.disconnect();
    };
  }, []);

  // 🔥 CRÍTICO: Reconectar socket cuando el app regresa a foco
  // Esto arregla: mensajes no se envían en chat, quórum no se sincroniza después de desbloquear móvil
  useEffect(() => {
    const handleVisibilityOrFocus = () => {
      const isVisible = document.visibilityState === 'visible';
      const hasToken = !!localStorage.getItem('accessToken');
      
      if (!isVisible || !hasToken) {
        return;
      }

      if (!isConnected) {
        console.log('⚡ [SocketContext] App regresó a foco y socket desconectado, reconectando...');
        socketService.reconnect();
        return;
      }

      const assemblyId = currentAssembly || socketService.getCurrentAssembly();
      if (assemblyId) {
        void syncLiveVotingState(assemblyId, { reason: 'visibility-or-focus' });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityOrFocus);
    window.addEventListener('focus', handleVisibilityOrFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
      window.removeEventListener('focus', handleVisibilityOrFocus);
    };
  }, [isConnected, currentAssembly, syncLiveVotingState]);

  // Cargar sesiones activas cuando nos unimos a una asamblea (útil si el admin entra tarde)
  useEffect(() => {
    if (!currentAssembly) return;
    void syncLiveVotingState(currentAssembly, { force: true, reason: 'assembly-changed' });
  }, [currentAssembly, syncLiveVotingState]);

  // Actualizar participantsCount cuando cambian los participantes elegibles (delegaciones)
  useEffect(() => {
    const handleEligibleParticipantsChanged = async () => {
      if (!currentAssembly) return;
      
      try {
        console.log('👥 [SocketContext] Actualizando participantsCount por cambio en delegaciones...');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/quorum/assembly/${currentAssembly}/status/manager-view`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        });
        
        if (response.ok) {
          const quorumData = await response.json();
          const presentUsers = parseInt((quorumData.quorumStatus || quorumData).presentUsers) || 0;
          console.log('✅ [SocketContext] participantsCount actualizado a:', presentUsers);
          setParticipantsCount(presentUsers);
        }
      } catch (error) {
        console.error('❌ [SocketContext] Error al actualizar participantsCount:', error);
      }
    };

    window.addEventListener('eligible:participants-changed', handleEligibleParticipantsChanged);
    
    return () => {
      window.removeEventListener('eligible:participants-changed', handleEligibleParticipantsChanged);
    };
  }, [currentAssembly]);

  const setupSocketListeners = () => {
    // Eventos de conexión
    socketService.on('socket:connected', () => {
      setIsConnected(true);
      setConnectionError(null);
      console.log('✅ Socket conectado');

      const assemblyId = socketService.getCurrentAssembly();
      if (assemblyId) {
        void syncLiveVotingState(assemblyId, { reason: 'socket-connected' });
      }
    });

    socketService.on('socket:disconnected', (data: { reason: string }) => {
      setIsConnected(false);
      // No limpiamos currentAssembly aquí para preservar estado durante reconexiones automáticas.
      // Si se limpia, el siguiente assembly:joined se interpreta como cambio de asamblea y borra el chat.
      console.log('❌ Socket desconectado:', data.reason);
    });

    socketService.on('socket:error', (error: any) => {
      setConnectionError(error.message || 'Error de conexión');
      console.error('❌ Error del socket:', error);
    });

    socketService.on('socket:max-reconnect-attempts', () => {
      setConnectionError('No se pudo conectar al servidor. Intenta recargar la página.');
    });

    // Eventos de autenticación
    socketService.on('auth:error', (error: any) => {
      setConnectionError('Error de autenticación. Por favor, inicia sesión nuevamente.');
      console.error('❌ Error de autenticación:', error);
    });

    // Eventos de asamblea
    socketService.on('assembly:joined', (data: { assemblyId: string; participants?: number }) => {
      traceRealtime('assembly.joined.event', {
        assemblyId: data.assemblyId,
        participants: data.participants,
      });
      setCurrentAssembly((previousAssemblyId) => {
        const isAssemblyChanged = previousAssemblyId !== data.assemblyId;

        // Solo limpiar chat si realmente cambió de asamblea.
        // En rejoin de la misma asamblea (reconexión) no se debe borrar el estado del chat.
        if (isAssemblyChanged) {
          setChatMessages([]);
          setTypingUsers([]);
        }

        return data.assemblyId;
      });

      // Actualizar contador de participantes si viene en el evento
      if (data.participants !== undefined) {
        setParticipantsCount(data.participants);
      }

      // Rejoin en misma asamblea no cambia currentAssembly; forzamos resync para no perder eventos en móvil.
      void syncLiveVotingState(data.assemblyId, { force: true, reason: 'assembly-joined' });

      console.log('✅ Unido a asamblea:', data.assemblyId, `- Participantes: ${data.participants || 0}`);
    });

    socketService.on('assembly:left', () => {
      setCurrentAssembly(null);
      setChatMessages([]);
      setTypingUsers([]);
      setQuorumStatus(null);
      setActiveVotingSession(null);
      setActiveSessions([]); // ✅ Limpiar lista de sesiones activas
      setVotingResults(null);
      setParticipantsCount(0);
      console.log('👋 Salió de asamblea');
    });

    // Evento cuando el administrador inicia una asamblea
    socketService.on('assembly:started', (data: { assembly?: any }) => {
      const assemblyId = data.assembly?.id;
      console.log('🚀 [SocketContext] assembly:started recibido - Asamblea iniciada:', assemblyId);
      if (assemblyId) {
        setLastAssemblyStartedId(assemblyId);
      }
    });

    // Evento cuando la asamblea se completa
    socketService.on('assembly:completed', (data: { assembly?: any }) => {
      const assemblyId = data.assembly?.id;
      console.log('✅ [SocketContext] assembly:completed recibido - Asamblea completada:', assemblyId);
      console.log('📋 [SocketContext] Datos completos recibidos:', data);
      if (assemblyId) {
        console.log('🔄 [SocketContext] Actualizando lastAssemblyCompletedId a:', assemblyId);
        setLastAssemblyCompletedId(assemblyId);
        console.log('✅ [SocketContext] lastAssemblyCompletedId actualizado');
      } else {
        console.warn('⚠️ [SocketContext] No se encontró assemblyId en el evento de completado');
      }
    });

    // Eventos de participantes
    socketService.on('user:joined', (data: { userId: string; userName: string; participants: number }) => {
      console.log(`👤 [SocketContext] user:joined recibido - Usuario ${data.userName} se união. Total: ${data.participants}`);
      setParticipantsCount(data.participants);
      console.log(`📊 [SocketContext] participantsCount actualizado a ${data.participants}`);
    });

    socketService.on('user:rejoined', (data: { userId: string; userName: string; participants: number }) => {
      console.log(`🔄 [SocketContext] user:rejoined recibido - Usuario ${data.userName} volvió. Total: ${data.participants}`);
      setParticipantsCount(data.participants);
    });

    socketService.on('user:left', (data: { userId: string; userName: string; participants: number }) => {
      console.log(`👋 [SocketContext] user:left recibido - Usuario ${data.userName} salió. Total: ${data.participants}`);
      setParticipantsCount(data.participants);
      console.log(`📊 [SocketContext] participantsCount actualizado a ${data.participants}`);
    });

    // Eventos de quórum
    socketService.on('quorum:status-updated', (data: any) => {
      traceRealtime('quorum.status.updated.event', {
        assemblyId: data?.quorum?.assemblyId,
        presentUsers: data?.quorum?.presentUsers,
        quorumAchieved: data?.quorum?.quorumAchieved,
      });
      console.log('📊 [SocketContext] quorum:status-updated recibido:', data);
      if (data.quorum) {
        setQuorumStatus(data.quorum);
      }
    });

    socketService.on('quorum:attendance-registered', (data: any) => {
      console.log('👤 [SocketContext] quorum:attendance-registered recibido:', data);
      if (data.quorum) {
        setQuorumStatus(data.quorum);
      }
    });

    socketService.on('quorum:attendance-updated', (data: any) => {
      console.log('🔄 [SocketContext] quorum:attendance-updated recibido:', data);
      if (data.quorum) {
        setQuorumStatus(data.quorum);
      }
    });

    socketService.on('quorum:achieved', (data: any) => {
      console.log('✅ [SocketContext] quorum:achieved recibido:', data);
      if (data.quorum) {
        setQuorumStatus(data.quorum);
      }
    });

    socketService.on('quorum:lost', (data: any) => {
      console.log('❌ [SocketContext] quorum:lost recibido:', data);
      if (data.quorum) {
        setQuorumStatus(data.quorum);
      }
    });

    // Evento de quórum actualizado por delegaciones
    socketService.on('quorum:updated', (quorumData: QuorumStatus) => {
      console.log('🔄 [SocketContext] quorum:updated recibido (delegación):', quorumData);
      setQuorumStatus(quorumData);
    });

    // Eventos de delegación
    socketService.on('delegation:received', (data: any) => {
      console.log('🤝 [SocketContext] delegation:received recibido:', data);
      // Mostrar notificación al usuario
      setNotifications(prev => [...prev, {
        id: `delegation-${data.delegationId}`,
        type: 'info',
        priority: 'high',
        title: 'Delegación Recibida',
        message: `${data.delegatorName} te ha delegado su voto (${data.coefficient} coeficiente)`,
        timestamp: new Date(),
        read: false
      }]);
    });

    socketService.on('delegation:revoked', (data: any) => {
      console.log('❌ [SocketContext] delegation:revoked recibido:', data);
      // Mostrar notificación de revocación
      setNotifications(prev => [...prev, {
        id: `delegation-revoked-${data.delegationId}`,
        type: 'warning',
        priority: 'medium',
        title: 'Delegación Revocada',
        message: `${data.delegatorName} ha revocado su delegación (${data.coefficient} coeficiente)`,
        timestamp: new Date(),
        read: false
      }]);
    });

    // Evento de cambio en participantes elegibles (para recargar en admin)
    socketService.on('eligible:participants-changed', (data: any) => {
      console.log('👥 [SocketContext] eligible:participants-changed recibido:', data);
      // Este evento sirve como trigger para que los componentes recarguen
    });

    // Eventos de votación
    socketService.on('voting:session-created', (session: VotingSessionData) => {
      console.log('🎯 [SocketContext] voting:session-created recibido:', session);
      setActiveVotingSession(session);
      setVotingResults(null); // Limpiar resultados de votación anterior
      
      // ✅ Agregar a la lista de sesiones activas
      setActiveSessions(prev => {
        const exists = prev.find(s => s.id === session.id);
        if (!exists && session.status === 'active') {
          return [...prev, session];
        }
        return prev;
      });
      console.log('✅ [SocketContext] voting:session-created procesado, votingResults limpiado');
    });

    // Escuchar actualizaciones de votación
    socketService.on('voting:session-updated', (data: any) => {
      // El backend envía { votingSession: ... }
      const session = data.votingSession || data;
      console.log('🔄 [SocketContext] voting:session-updated recibido:', session);
      
      // Actualizar si es la sesión activa
      setActiveVotingSession(prev => (prev?.id === session.id ? session : prev));
      
      // Actualizar en la lista de sesiones activas
      setActiveSessions(prev => {
        const index = prev.findIndex(s => s.id === session.id);
        if (index >= 0 && session.status === 'active') {
          const updated = [...prev];
          updated[index] = session;
          return updated;
        }

        if (index >= 0 && session.status !== 'active') {
          return prev.filter(s => s.id !== session.id);
        }

        if (index < 0 && session.status === 'active') {
          return [...prev, session];
        }

        return prev;
      });
      
      console.log('✅ [SocketContext] voting:session-updated procesado');
    });

    socketService.on('voting:session-started', (data: any) => {
      // El backend envía { votingSession: ... }
      const session = data.votingSession || data;
      console.log('🎯 [SocketContext] voting:session-started recibido:', session);
      setActiveVotingSession(session);
      setVotingResults(null); // Limpiar resultados anteriores
      
      // ✅ Actualizar en la lista de sesiones activas
      setActiveSessions(prev => {
        const index = prev.findIndex(s => s.id === session.id);
        if (index >= 0) {
          // Actualizar existente
          const updated = [...prev];
          updated[index] = session;
          return updated;
        } else if (session.status === 'active') {
          // Agregar nueva
          return [...prev, session];
        }
        return prev;
      });
    });

    socketService.on('voting:session-ended', (data: { votingSession?: VotingSessionData; session?: VotingSessionData; results?: VotingResults }) => {
      const session = data?.votingSession || data?.session;
      console.log('🎯 [SocketContext] voting:session-ended recibido:', data);
      console.log('🎯 [SocketContext] Sesión:', session);
      console.log('🎯 [SocketContext] Resultados:', data?.results);

      if (session) {
        setActiveVotingSession(session);
      }
      if (data?.results) {
        console.log('📊 [SocketContext] Estableciendo votingResults con', data.results.results.length, 'opciones');
        setVotingResults(data.results);
      }

      // ✅ Remover de la lista de sesiones activas cuando termina, con guard
      if (session?.id) {
        console.log('🗑️ [SocketContext] Removiendo sesión', session.id, 'de activeSessions');
        setActiveSessions(prev => {
          const filtered = prev.filter(s => s.id !== session!.id);
          console.log('📋 [SocketContext] activeSessions después de remover:', filtered.length, 'sesiones');
          return filtered;
        });
      } else {
        console.warn('⚠️ [SocketContext] votingSession.id ausente en evento voting:session-ended; no se modifica activeSessions');
      }
    });

    // Actualización de conteo en vivo (sin revelar resultados); útil para admin
    socketService.on('voting:vote-count-updated', (data: { votingSessionId: string; totalVotes: number; totalEligibleVoters?: number; participationRate?: number }) => {
      console.log('🎯 [SocketContext] voting:vote-count-updated recibido:', data);
      setVotingResults(prev => {
        // Crear estructura mínima si no existe
        if (!prev || prev.votingSessionId !== data.votingSessionId) {
          console.log('🆕 [SocketContext] Creando estructura de votingResults para sesión:', data.votingSessionId);
          return {
            votingSessionId: data.votingSessionId,
            status: 'active',
            totalEligibleVoters: data.totalEligibleVoters || 0,
            totalVotes: data.totalVotes,
            totalCoefficient: 0,
            votedCoefficient: 0,
            participationRate: data.participationRate || 0,
            quorumMet: false,
            results: [],
          } as VotingResults;
        }
        console.log('📊 [SocketContext] Actualizando totalVotes a', data.totalVotes, 'participationRate:', data.participationRate);
        return { 
          ...prev, 
          totalVotes: data.totalVotes,
          totalEligibleVoters: data.totalEligibleVoters || prev.totalEligibleVoters,
          participationRate: data.participationRate !== undefined ? data.participationRate : prev.participationRate
        };
      });
    });

    // Escuchar resultados finales (evento alternativo)
    socketService.on('voting:final-results', (data: { votingSession: VotingSessionData; results: VotingResults }) => {
      console.log('🎯 [SocketContext] voting:final-results recibido:', data);
      setActiveVotingSession(data.votingSession);
      setVotingResults(data.results);
    });

    // ✅ Listener de resultados en vivo eliminado - solo mostrar al finalizar
    // Mejora la eficiencia y evita influenciar votos pendientes

    // Eventos de chat
    socketService.on('chat:message', (message: ChatMessage) => {
      traceRealtime('chat.message.event', {
        messageId: message.id,
        assemblyId: message.assemblyId,
        userId: message.userId,
      });
      setChatMessages(prev => {
        if (prev.some(existing => existing.id === message.id)) {
          return prev;
        }
        return [...prev, message];
      });
    });

    socketService.on('chat:typing', (data: { userId: string; userName: string; isTyping: boolean; assemblyId?: string }) => {
      setTypingUsers(prev => {
        const filtered = prev.filter(user => user.userId !== data.userId);
        return data.isTyping 
          ? [...filtered, { userId: data.userId, userName: data.userName, assemblyId: data.assemblyId }]
          : filtered;
      });
    });

    // Eventos de notificaciones
    socketService.on('notification:new', (notification: NotificationData) => {
      setNotifications(prev => [notification, ...prev]);
    });

    socketService.on('notification:assembly', (notification: NotificationData) => {
      setNotifications(prev => [notification, ...prev]);
    });

    socketService.on('notification:system', (notification: NotificationData) => {
      setNotifications(prev => [notification, ...prev]);
    });

    // Eventos de presencia
    socketService.on('presence:updated', (data: { userId: string; userName: string; status: string }) => {
      setPresenceUsers(prev => {
        const filtered = prev.filter(user => user.id !== data.userId);
        const user: PresenceUser = {
          id: data.userId,
          name: data.userName,
          email: '', // Se podría obtener de otra fuente
          role: '',
          isPresent: data.status === 'present'
        };
        return [...filtered, user];
      });
    });

    // Eventos de sistema
    socketService.on('force:disconnect', (data: { reason: string }) => {
      setConnectionError(`Desconectado: ${data.reason}`);
    });
  };

  // Métodos de conexión
  const connect = async (): Promise<void> => {
    try {
      setConnectionError(null);
      await socketService.connect();
    } catch (error: any) {
      setConnectionError(error.message || 'Error al conectar');
    }
  };

  const disconnect = (): void => {
    socketService.disconnect();
    setIsConnected(false);
    setCurrentAssembly(null);
  };

  const reconnect = (): void => {
    setConnectionError(null);
    socketService.reconnect();
  };

  // Métodos de asamblea
  const joinAssembly = async (assemblyId: string): Promise<void> => {
    try {
      await socketService.joinAssembly(assemblyId);
    } catch (error: any) {
      console.error('Error al unirse a asamblea:', error);
      throw error;
    }
  };

  const leaveAssembly = async (assemblyId: string): Promise<void> => {
    try {
      console.log(`🚪 [SocketContext.leaveAssembly] Saliendo de asamblea ${assemblyId}...`);
      
      // 1. Primero actualizar la BD vía REST
      console.log(`📝 [SocketContext.leaveAssembly] Llamando POST /api/assemblies/${assemblyId}/leave...`);
      await apiService.leaveAssembly(assemblyId);
      console.log(`✅ [SocketContext.leaveAssembly] REST endpoint llamado exitosamente`);
      
      // 2. Luego notificar al socket
      console.log(`📡 [SocketContext.leaveAssembly] Emitiendo leave:assembly vía socket...`);
      await socketService.leaveAssembly(assemblyId);
      console.log(`✅ [SocketContext.leaveAssembly] Socket notificado exitosamente`);
    } catch (error: any) {
      console.error('❌ Error al salir de asamblea:', error);
    }
  };

  // Métodos de chat
  const sendMessage = async (assemblyId: string, message: string): Promise<void> => {
    await socketService.sendChatMessage(assemblyId, message);
  };

  const setTyping = (assemblyId: string, isTyping: boolean): void => {
    socketService.sendTypingIndicator(assemblyId, isTyping);
  };

  // Métodos de votación
  // ✅ Funciones deprecadas - Ya no se usan resultados en vivo
  // Los resultados se muestran solo cuando la votación finaliza
  const subscribeToVoting = (votingSessionId: string): void => {
    // No hace nada - deprecado
    console.log('⚠️ subscribeToVoting deprecado - resultados solo al finalizar');
  };

  const unsubscribeFromVoting = (votingSessionId: string): void => {
    // No hace nada - deprecado
    console.log('⚠️ unsubscribeFromVoting deprecado - resultados solo al finalizar');
  };

  // Métodos de presencia
  const updatePresence = (assemblyId: string, status: 'present' | 'absent'): void => {
    socketService.updatePresence(assemblyId, status);
  };

  // Métodos de notificaciones
  const markNotificationAsRead = (notificationId: string): void => {
    socketService.markNotificationAsRead(notificationId);
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === notificationId 
          ? { ...notif, read: true }
          : notif
      )
    );
  };

  const clearNotifications = (): void => {
    setNotifications([]);
  };

  // 🆕 Métodos para limpiar eventos de asamblea (prevenir loops infinitos)
  const clearAssemblyStartedEvent = useCallback((): void => {
    console.log('🧹 [SocketContext] Limpiando lastAssemblyStartedId');
    setLastAssemblyStartedId(null);
  }, []);

  const clearAssemblyCompletedEvent = useCallback((): void => {
    console.log('🧹 [SocketContext] Limpiando lastAssemblyCompletedId');
    setLastAssemblyCompletedId(null);
  }, []);

  // 🔥 Método para cargar mensajes históricos desde la API
  const loadHistoricalMessages = useCallback((messages: ChatMessage[]): void => {
    setChatMessages(messages);
  }, []);

  const contextValue: SocketContextType = {
    // Estado
    isConnected,
    currentAssembly,
    connectionError,
    
    // Métodos de conexión
    connect,
    disconnect,
    reconnect,
    
    // Métodos de asamblea
    joinAssembly,
    leaveAssembly,
    
    // Estado en tiempo real
    quorumStatus,
    activeVotingSession,
    activeSessions, // ✅ Exportar lista de sesiones activas
    votingResults,
    notifications,
    chatMessages,
    typingUsers,
    presenceUsers,
    participantsCount,
    
    // 🆕 Eventos de asamblea
    lastAssemblyStartedId,
    lastAssemblyCompletedId,
    
    // 🆕 Métodos para limpiar eventos
    clearAssemblyStartedEvent,
    clearAssemblyCompletedEvent,
    
    // Métodos
    sendMessage,
    setTyping,
    loadHistoricalMessages, // 🔥 Agregar método
    subscribeToVoting,
    unsubscribeFromVoting,
    updatePresence,
    markNotificationAsRead,
    clearNotifications,
  };

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
};

// Hook personalizado para usar el contexto
export const useSocket = (): SocketContextType => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket debe ser usado dentro de un SocketProvider');
  }
  return context;
};

export default SocketContext;